import { yupResolver } from '@hookform/resolvers/yup'
import { map } from 'lodash-es'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import {
  Button,
  Checkbox,
  Input,
  Modal,
  RadioGroup,
  RadioGroupItem,
} from '@/components/ui'
import {
  RefundExtraPayload,
  RefundTicket,
  RefundTicketPayload,
} from '../../../common/models/orders'
import FormGroup from '../../../core/components/FormGroup'
import { updateRefundOrderTicket } from '../../../core/services/orders'
import { MessageBox } from '../messages/MessageBox'

export interface ICancelTicketValues {
  ticketPriceEnabled: boolean
  addons: {
    addonPriceEnabled: boolean
  }[]
  oneTimeFeeEnabled: boolean
  topUpPriceEnabled: boolean
  cancelTickets: boolean
  runa: 'true' | 'false'
}

export interface CancelOrderModalProps {
  showCancelModal: boolean
  setShowCancelModal: (show: boolean) => void
  ticket?: any
  withRuna: boolean
  orderId: string
  currency: string
}

export const CancelTicketModal = (
  props: CancelOrderModalProps,
): JSX.Element | null => {
  const {
    showCancelModal,
    withRuna,
    setShowCancelModal,
    ticket,
    orderId,
    currency,
  } = props
  const [fullRefund, setFullRefund] = useState<number>(0)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const schema = yup.object().shape({
    ticketPriceEnabled: yup.boolean().required(),
    oneTimeFeeEnabled: yup.boolean().required(),
    addons: yup
      .array()
      .of(
        yup.object().shape({
          addonPriceEnabled: yup.boolean().required(),
        }),
      )
      .required(),
    topUpPriceEnabled: yup.boolean().required(),
    cancelTickets: yup.boolean().required(),
    runa: yup.string().oneOf(['true', 'false']).required(),
  })

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<ICancelTicketValues>({
    mode: 'onChange',
    resolver: yupResolver<ICancelTicketValues>(schema),
    defaultValues: {
      cancelTickets: false,
      topUpPriceEnabled: false,
      oneTimeFeeEnabled: false,
      ticketPriceEnabled: false,
    },
  })

  const ticketPriceEnabledWatch = watch('ticketPriceEnabled')
  const topUpPriceEnabledWatch = watch('topUpPriceEnabled')
  const oneTimeFeeEnabledWatch = watch('oneTimeFeeEnabled')
  const addonWatch = watch('addons')

  const handleClose = () => {
    reset()
    setShowCancelModal(false)
  }

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <div className="mb-5 text-title-s font-bold leading-none text-monochrome-1">
      {children}
    </div>
  )

  const submit = async (values: ICancelTicketValues) => {
    try {
      const extras = values.addons
        .map((addon, index) => {
          if (addon.addonPriceEnabled) {
            return {
              extraId: ticket.refundExtras[index].extrasId,
              amountDefaultCurrency:
                ticket.refundExtras[index].priceDefaultCurrency,
            }
          } else {
            return null
          }
        })
        .filter((extra) => extra !== null)
      const payload: RefundTicketPayload = {
        ticketsPriceDefaultCurrency: values.ticketPriceEnabled
          ? ticket.ticketPriceDefaultCurrency
          : 0,
        topupAmountDefaultCurrency: values.topUpPriceEnabled
          ? ticket.topupAmountDefaultCurrency
          : 0,
        oneTimeFeeDefaultCurrency: values.oneTimeFeeEnabled
          ? ticket.oneTimeFeeDefaultCurrency
          : 0,
        cancelTicket: values.cancelTickets,
        refundExtras: extras as RefundExtraPayload[],
        type: withRuna ? 'COINS' : 'CARD',
      }

      const { status } = await updateRefundOrderTicket(
        orderId,
        ticket.id,
        payload,
      )
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
    handleClose()
  }

  useEffect(() => {
    if (ticket) {
      const sum = map(ticket.refundExtras, (addon, index) => {
        return addonWatch[index] ? addon.priceDefaultCurrency : 0
      })
      const addonSum =
        sum.length > 0 ? sum.reduce((acc, current) => acc + current) : 0
      const oneTime = oneTimeFeeEnabledWatch
        ? ticket.oneTimeFeeDefaultCurrency
        : 0
      const topUpSum = topUpPriceEnabledWatch
        ? ticket.topupAmountDefaultCurrency
        : 0
      const ticketSum = ticketPriceEnabledWatch
        ? ticket.ticketPriceDefaultCurrency
        : 0

      setFullRefund(addonSum + oneTime + topUpSum + ticketSum)
    }
  }, [
    ticketPriceEnabledWatch,
    oneTimeFeeEnabledWatch,
    topUpPriceEnabledWatch,
    addonWatch,
  ])

  useEffect(() => {
    if (ticket) {
      setValue('runa', withRuna ? 'true' : 'false')
    }

    return () => {
      reset()
    }
  }, [ticket, showCancelModal])

  if (!ticket) {
    return null
  }

  return (
    <>
      <Modal
        open={showCancelModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              title="Cancel & Refund"
              subtitle={
                ticket
                  ? `${ticket.barcode[0]} - ${ticket.boughtForName}`
                  : 'Ticket'
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2">
            <Button onClick={handleClose} type="button" variant="secondary">
              Close
            </Button>
            <Button
              form="cancel-order-form"
              type="submit"
              variant="destructive"
            >
              Cancel & Refund
            </Button>
          </div>
        }
      >
        <form
          id="cancel-order-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <SectionTitle>Refund Options</SectionTitle>

          <div className="flex flex-col gap-3 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50 p-4">
            <Controller
              name="runa"
              control={control}
              render={({ field }) => (
                <FormGroup label="Refund Type">
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="flex flex-col gap-2"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="true" id="runa-true" />
                      <label
                        htmlFor="runa-true"
                        className="text-sm font-medium text-monochrome-1 cursor-pointer"
                      >
                        Cancel & refund with RUNA
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="false" id="runa-false" />
                      <label
                        htmlFor="runa-false"
                        className="text-sm font-medium text-monochrome-1 cursor-pointer"
                      >
                        Cancel without RUNA
                      </label>
                    </div>
                  </RadioGroup>
                </FormGroup>
              )}
            />
          </div>

          <SectionTitle>Refund Items</SectionTitle>

          <div className="flex flex-col gap-3 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50 p-4">
            <FormGroup
              label={`Tickets Total Price in ${currency}`}
              direction="row"
              labelClassName="flex-1"
            >
              <Controller
                name="ticketPriceEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
            </FormGroup>
            <FormGroup label="Price">
              <Input
                type="number"
                disabled
                value={ticket.ticketPriceDefaultCurrency}
              />
            </FormGroup>
          </div>

          <div className="flex flex-col gap-3 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50 p-4">
            <FormGroup
              label="One Time Fee"
              direction="row"
              labelClassName="flex-1"
            >
              <Controller
                name="oneTimeFeeEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
            </FormGroup>
            <FormGroup label="Price">
              <Input
                type="number"
                disabled
                value={ticket.oneTimeFeeDefaultCurrency}
              />
            </FormGroup>
          </div>

          {map(ticket.refundExtras, (addon, index) => (
            <div
              key={index}
              className="flex flex-col gap-3 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50 p-4"
            >
              <FormGroup
                label={addon.extrasName}
                direction="row"
                labelClassName="flex-1"
              >
                <Controller
                  name={`addons.${index}.addonPriceEnabled` as any}
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </FormGroup>
              <FormGroup label="Price">
                <Input
                  type="number"
                  disabled
                  value={addon.priceDefaultCurrency}
                />
              </FormGroup>
            </div>
          ))}

          <div className="flex flex-col gap-3 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50 p-4">
            <FormGroup label="Top Up" direction="row" labelClassName="flex-1">
              <Controller
                name="topUpPriceEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
            </FormGroup>
            <FormGroup label="Price">
              <Input
                type="number"
                disabled
                value={ticket.topupAmountDefaultCurrency}
              />
            </FormGroup>
          </div>

          <div className="my-4 h-px w-full bg-monochrome-120" />

          <FormGroup label={`Total to be refunded (${currency})`}>
            <Input type="number" disabled value={fullRefund} />
          </FormGroup>

          <FormGroup
            label="Cancel Tickets"
            direction="row"
            labelClassName="flex-1"
          >
            <Controller
              name="cancelTickets"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </FormGroup>
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
